import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { useQuery } from "react-query";
import { Activity } from "lucide-react";
import { getFunnelStats, getProductEvents, getTopPaths } from "services/productEvent.service";
import { KNOWN_FUNNELS } from "types/productEvent.type";

const FUNNEL_LABELS: Record<string, string> = {
  navigation: "All Page Views",
  signup: "Signup",
  kyc: "KYC / Profile Setup",
  job_apply: "Job Application",
  hire: "Hire & Payment",
};

const eventLabel = (event: string) =>
  event.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ProductEvents = () => {
  const [funnel, setFunnel] = useState<string>(KNOWN_FUNNELS[0]);
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: funnelRes, isLoading: funnelLoading } = useQuery(
    ["funnel-stats", funnel],
    () => getFunnelStats(funnel, {})
  );
  const { data: topPathsRes } = useQuery(
    ["top-paths"],
    () => getTopPaths({}),
    { enabled: funnel === "navigation" }
  );
  const { data: eventsRes, isLoading: eventsLoading } = useQuery(
    ["product-events", funnel, page],
    () => getProductEvents({ page, limit }, { funnel })
  );

  const steps = funnelRes?.data?.steps ?? [];
  const events = eventsRes?.data?.data ?? [];
  const total = eventsRes?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const maxCount = steps.length ? Math.max(...steps.map((s) => s.count), 1) : 1;

  return (
    <React.Fragment>
      <BreadCrumb title="User Activity" pageTitle="User Activity" />

      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm mb-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-gray-800">Funnel drop-off</p>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            value={funnel}
            onChange={(e) => {
              setFunnel(e.target.value);
              setPage(1);
            }}
          >
            {KNOWN_FUNNELS.map((f) => (
              <option key={f} value={f}>{FUNNEL_LABELS[f] ?? f}</option>
            ))}
          </select>
        </div>

        {funnelLoading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading funnel…</div>
        ) : !steps.some((s) => s.count > 0) ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No events recorded for this funnel yet.
          </div>
        ) : (
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <div key={step.event}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-gray-700">
                    {idx + 1}. {eventLabel(step.event)}
                  </span>
                  <span className="text-gray-500">
                    {step.count} · {step.uniqueUsers} people · {step.conversionFromStart}% of start
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-indigo-500"
                    style={{ width: `${Math.max(2, (step.count / maxCount) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {funnel === "navigation" && !!topPathsRes?.data?.length && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm mb-5">
          <p className="text-sm font-semibold text-gray-800 mb-4">Top pages by traffic</p>
          <div className="space-y-2">
            {topPathsRes.data.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm">
                <span className="font-mono text-gray-700 truncate pr-3">{p.path}</span>
                <span className="text-gray-500 whitespace-nowrap">
                  {p.count} views · {p.uniqueUsers} people
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="px-5 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-800">Recent events — {FUNNEL_LABELS[funnel] ?? funnel}</p>
        </div>
        {eventsLoading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Activity size={22} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No activity recorded yet</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">When</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Event</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Step</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Path</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map((ev) => (
                    <tr key={ev._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(ev.createdAt).toLocaleDateString()}, {new Date(ev.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{eventLabel(ev.event)}</td>
                      <td className="px-4 py-3 text-gray-600">{ev.step ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {ev.user
                          ? `${ev.user.firstName ?? ""} ${ev.user.lastName ?? ""}`.trim() || ev.user.email
                          : "Anonymous"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{ev.role ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{ev.path ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Page {page} of {totalPages} · {total} total
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
};

export default ProductEvents;
